// ────────────────────────────────────────────────────────────────────
// GuestLandingScreen — the public `/guest/:token` route.
//
// docs/superpowers/specs/2026-07-23-guest-access-design.md, frontend PR 2.
//
// Mounted OUTSIDE the authed shell in App.tsx (like /login) — no menu bar,
// no left tree, no dock, no other navigation. A guest must never be able to
// wander: this component renders EXACTLY the scoped asset or submission and
// nothing else reachable from it.
//
// Flow: redeem the invite token → store the returned guest JWT under the
// SEPARATE xzynexa.guestToken key (guestApi/setGuestToken in lib/api.ts —
// never the staff/customer xzynexa.token, so a guest tab and a logged-in
// staff tab never cross-contaminate) → fetch the scoped read view through
// guestClient (the dedicated axios instance that only ever sends that
// separate key). All read views below REUSE the same renderers the
// customer/staff screens use (CmlIntegrityTable/CmlDetail from
// AssetWorkspaceScreen, EventRow/StatusPill/STATUS_META from
// JobScreen) — never a rebuilt copy, per the spec's "reuse
// read-only renderers" instruction.
// ────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ShieldOff, Eye, Clock, FileText, Download } from "lucide-react";
import { guestClient, setGuestToken } from "@/lib/api";
import type { CmlRow, GuestRedeemOut, IntakeFile } from "@/lib/types";
import { CmlIntegrityTable, CmlDetail } from "./AssetWorkspaceScreen";
import { EventRow, StatusPill, STATUS_META, fmtWhen } from "./JobScreen";

/** Read the `exp` claim straight out of the guest JWT for display — the
 *  redeem response itself doesn't carry expires_at (GuestRedeemOut in
 *  server/routers/guest.py deliberately keeps that response minimal), and
 *  the JWT's own `exp` IS the authoritative expiry (guest_auth.py mints it
 *  as min(remaining grant, 12h)). No signature check needed here — this is
 *  a display convenience, not a trust boundary; every guest-reachable read
 *  re-validates the token server-side on every request regardless. */
function decodeJwtExp(token: string): Date | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as { exp?: number };
    return claims.exp ? new Date(claims.exp * 1000) : null;
  } catch {
    return null;
  }
}

type RedeemState =
  | { phase: "loading" }
  | { phase: "error" }
  | { phase: "ok"; data: GuestRedeemOut; expiresAt: Date | null };

/** One-shot redeem — not react-query, because storing the guest token is a
 *  side effect that must happen exactly once per successful redeem, and
 *  react-query v5 dropped onSuccess from useQuery. The scoped reads below
 *  DO use react-query, gated on this having reached "ok". */
function useRedeem(token: string | undefined): RedeemState {
  const [state, setState] = useState<RedeemState>({ phase: "loading" });
  useEffect(() => {
    let cancelled = false;
    if (!token) { setState({ phase: "error" }); return; }
    setState({ phase: "loading" });
    guestClient.redeem(token).then(
      (data) => {
        if (cancelled) return;
        setGuestToken(data.token);
        setState({ phase: "ok", data, expiresAt: decodeJwtExp(data.token) });
      },
      () => { if (!cancelled) setState({ phase: "error" }); },
    );
    return () => { cancelled = true; };
  }, [token]);
  return state;
}

export default function GuestLandingScreen(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const redeem = useRedeem(token);

  if (redeem.phase === "loading") {
    return <CenterPage><Eye size={22} style={{ color: "var(--ink-4)" }} /><p className="mt-3 font-mono" style={{ fontSize: 12, color: "var(--ink-4)" }}>กำลังตรวจสอบลิงก์…</p></CenterPage>;
  }
  if (redeem.phase === "error") {
    return (
      <CenterPage>
        <ShieldOff size={26} style={{ color: "var(--ink-4)" }} />
        <p className="mt-3 font-mono" style={{ fontSize: 14, color: "var(--ink-2)" }}>ลิงก์นี้หมดอายุหรือถูกยกเลิก</p>
        <p className="mt-1.5 font-mono text-center" style={{ fontSize: 11, color: "var(--ink-3)", maxWidth: 320, lineHeight: 1.6 }}>
          กรุณาติดต่อผู้ที่ส่งลิงก์นี้ให้คุณเพื่อขอลิงก์ใหม่
        </p>
      </CenterPage>
    );
  }

  return <GuestScopedView data={redeem.data} expiresAt={redeem.expiresAt} />;
}

function CenterPage({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center" style={{ background: "var(--paper)" }}>
      {children}
    </div>
  );
}

// ── The scoped view — header + asset-or-submission body ──
function GuestScopedView({ data, expiresAt }: { data: GuestRedeemOut; expiresAt: Date | null }): JSX.Element {
  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden" style={{ background: "var(--paper)" }}>
      {/* Minimal header — no nav, no menu, nothing to click that isn't this
          page's own content. */}
      <header className="px-6 py-3.5 border-b flex items-center justify-between gap-4 shrink-0" style={{ borderColor: "var(--hairline)" }}>
        <div className="min-w-0">
          <div className="font-mono uppercase flex items-center gap-1.5" style={{ fontSize: 9.5, letterSpacing: ".18em", color: "var(--accent-deep)", fontWeight: 600 }}>
            <Eye size={11} /> มุมมองผู้เยี่ยมชม · อ่านอย่างเดียว
          </div>
          <div className="font-display font-medium truncate mt-0.5" style={{ fontSize: 19, color: "var(--ink-1)" }}>
            {data.tenant_name}{data.label ? ` · ${data.label}` : ""}
          </div>
        </div>
        {expiresAt && (
          <div className="font-mono inline-flex items-center gap-1.5 shrink-0" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>
            <Clock size={11} /> หมดอายุ {expiresAt.toLocaleString()}
          </div>
        )}
      </header>

      <div className="flex-1 overflow-auto">
        {data.scope_type === "asset"
          ? <GuestAssetView assetId={data.scope_ref} />
          : <GuestSubmissionView subId={data.scope_ref} />}
      </div>
    </div>
  );
}

// ── Asset scope — the SAME CML view the customer sees, read-only ──
function GuestAssetView({ assetId }: { assetId: number }): JSX.Element {
  const assetQ = useQuery({ queryKey: ["guest-asset", assetId], queryFn: () => guestClient.asset(assetId) });
  const corrosionQ = useQuery({ queryKey: ["guest-asset-corrosion", assetId], queryFn: () => guestClient.assetCorrosion(assetId) });
  const filesQ = useQuery({ queryKey: ["guest-asset-files", assetId], queryFn: () => guestClient.assetFiles(assetId) });
  const [selCmlId, setSelCmlId] = useState<number | null>(null);

  const corrosion = corrosionQ.data;
  const selCml: CmlRow | null = useMemo(() => {
    if (!corrosion) return null;
    const explicit = corrosion.cmls.find((c) => c.cml_id === selCmlId);
    if (explicit) return explicit;
    const govNo = corrosion.rollup.governing?.cml_no;
    return (govNo ? corrosion.cmls.find((c) => c.cml_no === govNo) : undefined) ?? corrosion.cmls[0] ?? null;
  }, [corrosion, selCmlId]);

  if (assetQ.isPending || corrosionQ.isPending) {
    return <div className="p-8 text-center font-mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>กำลังโหลด…</div>;
  }
  if (assetQ.isError || !assetQ.data) {
    return <div className="p-8 text-center font-mono" style={{ fontSize: 12, color: "var(--err)" }}>ไม่พบข้อมูล asset นี้</div>;
  }
  const asset = assetQ.data;

  return (
    <div className="px-6 py-5 max-w-6xl mx-auto">
      <div className="mb-4">
        <div className="font-mono" style={{ fontSize: 22, color: "var(--accent-deep)", fontWeight: 700 }}>{asset.tag}</div>
        {asset.name && asset.name !== asset.tag && (
          <div className="font-sans" style={{ fontSize: 16, color: "var(--ink-1)" }}>{asset.name}</div>
        )}
      </div>

      <div className="font-mono uppercase mb-2" style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>/ CML INTEGRITY</div>
      <div className="grid gap-4 items-stretch mb-8" style={{ gridTemplateColumns: "minmax(320px,1.4fr) minmax(300px,1fr)" }}>
        {corrosion && <CmlIntegrityTable cmls={corrosion.cmls} govCml={corrosion.rollup.governing?.cml_no}
                                          selId={selCml?.cml_id} onSelect={(c) => setSelCmlId(c.cml_id)} />}
        <div className="border" style={{ borderColor: "var(--hairline)" }}>
          {selCml ? <CmlDetail c={selCml} /> : (
            <div className="h-full flex items-center justify-center p-6 text-xs font-mono text-center" style={{ color: "var(--ink-3)" }}>
              เลือก CML เพื่อดูแนวโน้มความหนา
            </div>
          )}
        </div>
      </div>

      <GuestDocuments files={filesQ.data ?? []} loading={filesQ.isPending} />
    </div>
  );
}

// ── Submission scope — the SAME conversation renderer the customer sees, read-only ──
function GuestSubmissionView({ subId }: { subId: number }): JSX.Element {
  const subQ = useQuery({ queryKey: ["guest-submission", subId], queryFn: () => guestClient.submission(subId) });
  const eventsQ = useQuery({ queryKey: ["guest-submission-events", subId], queryFn: () => guestClient.submissionEvents(subId) });

  if (subQ.isPending) {
    return <div className="p-8 text-center font-mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>กำลังโหลด…</div>;
  }
  if (subQ.isError || !subQ.data) {
    return <div className="p-8 text-center font-mono" style={{ fontSize: 12, color: "var(--err)" }}>ไม่พบข้อมูลงานนี้</div>;
  }
  const d = subQ.data;
  const events = eventsQ.data ?? [];

  return (
    <div className="px-6 py-5 max-w-3xl mx-auto">
      <div className="flex items-center justify-between gap-4 mb-1">
        <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>{d.ref}</div>
        <StatusPill status={d.status} />
      </div>
      <h1 className="font-display font-medium m-0 mb-4" style={{ fontSize: 22, color: "var(--ink-1)" }}>{d.title}</h1>

      {d.status === "completed" && d.outcome && (
        <div className="mb-5 px-3 py-2.5 font-mono" style={{ fontSize: 11.5, color: "var(--ok)", background: "rgba(21,128,61,.06)", border: "1px solid rgba(21,128,61,.35)" }}>
          <div className="uppercase mb-0.5" style={{ fontSize: 8.5, letterSpacing: ".18em" }}>ผลงาน</div>{d.outcome}
        </div>
      )}
      {d.certified_by && (
        <div className="mb-5 font-mono" style={{ fontSize: 10.5, color: "var(--ink-3)" }}>
          รับรองโดย {d.certified_by}{d.certified_cert ? ` · ${d.certified_cert}` : ""}{d.certified_at ? ` · ${fmtWhen(d.certified_at)}` : ""}
        </div>
      )}

      <GuestDocuments files={d.files} loading={false} />

      <div className="mt-7 pt-4 border-t" style={{ borderColor: "var(--hairline)" }}>
        <div className="font-mono uppercase mb-3" style={{ fontSize: 8.5, letterSpacing: ".18em", color: "var(--ink-4)" }}>
          บทสนทนา · {STATUS_META[d.status]?.label}
        </div>
        <div className="flex flex-col gap-2.5">
          {events.map((e) => <EventRow key={e.id} e={e} />)}
          {eventsQ.isSuccess && events.length === 0 && (
            <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>ยังไม่มีข้อความ</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Shared read-only document list (files + certified report) ──
// No upload/delete/file-to-asset affordances of any kind — a guest is
// read-only, and this component simply never renders them (the backend
// would 403 anyway, but the point is the UI must not even offer to try).
function GuestDocuments({ files, loading }: { files: IntakeFile[]; loading: boolean }): JSX.Element {
  return (
    <div>
      <div className="font-mono uppercase mb-3" style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>
        / เอกสาร · {loading ? "—" : files.length}
      </div>
      {loading && <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>กำลังโหลด…</div>}
      {!loading && files.length === 0 && (
        <div className="border p-5 text-center font-mono" style={{ fontSize: 11, color: "var(--ink-3)", borderColor: "var(--hairline)" }}>
          ยังไม่มีเอกสาร
        </div>
      )}
      {files.length > 0 && (
        <div className="border divide-y" style={{ borderColor: "var(--hairline)" }}>
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-2.5 px-3 py-2.5">
              <FileText size={14} style={{ color: f.kind === "report" ? "var(--accent-deep)" : "var(--ink-3)" }} />
              <div className="flex-1 min-w-0">
                <div className="font-mono truncate" style={{ fontSize: 12, color: "var(--ink-1)" }}>{f.filename}</div>
                {f.kind === "report" && (
                  <div className="font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: ".08em", color: "var(--accent-deep)" }}>รายงานที่รับรอง</div>
                )}
              </div>
              {f.download_url ? (
                <a href={f.download_url} target="_blank" rel="noreferrer"
                   className="font-mono uppercase inline-flex items-center gap-1 shrink-0 hover:text-[var(--accent)]"
                   style={{ fontSize: 9.5, letterSpacing: ".08em", color: "var(--accent-deep)" }}>
                  <Download size={11} /> ดาวน์โหลด
                </a>
              ) : (
                <span className="font-mono" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>—</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
